'use client';

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import Link from 'next/link';
import { isFilled } from '@prismicio/helpers';
import { Settings } from '@/app/lib/settings';
import { RichText } from '@/app/lib/RichText';
import { ConditionalLink } from '@/app/lib/ConditionalLink';
import { dashify, groupEventsByDate, sliceStyle, formatLink, formatTarget, formatTime } from '@/app/lib/utils';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { RevealTitle } from '@/app/lib/RevealTitle';
import { BackgroundImage } from "@/app/lib/ParallaxBackground";
import { MoreLink } from '@/app/lib/MoreLink';
import ScheduleFilters from '@/app/lib/ScheduleFilters';
import Chevron from '@/app/assets/images/icon_chevron.svg';
import styles from './schedule.module.scss';
import sharedStyles from '@/app/shared.module.scss';

/**
 * Groups events by collapsible_group_label within a list of events.
 * Falls back to a 'Default' group if the label is not filled.
 */
const groupEventsByLabel = (events, displayDate) => {
  const grouped = {};

  events.forEach(event => {
    // If collapsible_group_label is filled, use it. Otherwise, use the date for grouping the default items.
    const label = event?.collapsible_group_label || displayDate;
    if (!grouped[label]) {
      grouped[label] = {
        // Use the label if present, otherwise use the date string
        panelTitle: label,
        events: []
      };
    }
    grouped[label].events.push(event);
  });

  // Convert the grouped object into an array of group objects
  return Object.values(grouped);
};

const Schedule = ({ slice, pageData }) => {
  // openIndexes now tracks indices for all panels (one entry per date/label group)
  // We can simplify back to a single index array if we give each label group a unique index
  const [openIndexes, setOpenIndexes] = useState([]);
  const [filters, setFilters] = useState({ date: null, venue: null });

  // Helper function to get the best available start date/time (prefers datetime, falls back to date)
  const getStartDateTime = (event) => {
    // Check linked_session first, then event
    const linkedSession = event?.linked_session?.data;
    if (isFilled.timestamp(linkedSession?.start_date_and_time)) {
      return linkedSession.start_date_and_time;
    }
    if (isFilled.timestamp(event?.start_date_and_time)) {
      return event.start_date_and_time;
    }
    if (isFilled.date(linkedSession?.start_date)) {
      return linkedSession.start_date;
    }
    if (isFilled.date(event?.start_date)) {
      return event.start_date;
    }
    return null;
  };

  // Helper function to check if a datetime field has time component
  const hasTimeComponent = (event, fieldName) => {
    const linkedSession = event?.linked_session?.data;
    return isFilled.timestamp(linkedSession?.[fieldName]) || isFilled.timestamp(event?.[fieldName]);
  };

  const allGroupedEvents = useMemo(() => {
    // Filter out events with only_show_on_full_list
    const filteredEvents = (pageData?.data?.event || []).filter(event => !event?.only_show_on_full_list);
    return groupEventsByDate(filteredEvents);
  }, [pageData?.data?.event]);

  // filteredEvents now stores the fully grouped structure (date -> label)
  const [filteredEvents, setFilteredEvents] = useState([]);

  // A helper to generate a unique key/index for each panel (date + label group)
  const getPanelKey = (dateIndex, labelIndex) => `${dateIndex}-${labelIndex}`;

  useEffect(() => {
    const { date, venue } = filters;
    // 1. Filter out events with only_show_on_full_list
    const eventsToProcess = (pageData?.data?.event || []).filter(event => !event?.only_show_on_full_list);

    // 2. Filter and group by Date using the existing utility
    const dateGrouped = groupEventsByDate(eventsToProcess, date, venue);

    // 3. Further group by Label within each date group
    const fullyGrouped = dateGrouped.map(dateGroup => ({
      ...dateGroup,
      // Group the events by label, passing the displayDate for fallback title
      labelGroups: groupEventsByLabel(dateGroup.events, dateGroup.displayDate)
    }));

    setFilteredEvents(fullyGrouped);
    // Reset open indexes on filter change
    setOpenIndexes([]);
  }, [filters, pageData?.data?.event]);

  // Toggle logic updated to use the unique key
  const togglePanel = useCallback((key) => {
    setOpenIndexes((prevOpenIndexes) => {
      if (prevOpenIndexes.includes(key)) {
        return prevOpenIndexes.filter((i) => i !== key);
      } else {
        return [...prevOpenIndexes, key];
      }
    });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  return (
    <BackgroundImage slicePrimary={slice?.primary}>
      <div className={clsx(styles.schedule)} style={sliceStyle(slice?.primary)}>

        {/* title */}
        <div className={clsx(sharedStyles.top)}>
          <div className={clsx(sharedStyles.left)}>
            {isFilled.keyText(slice?.primary?.title) && (
              <h1 className={clsx(sharedStyles.h1)} id={dashify(slice?.primary?.title)}>
                <RevealTitle size='small'>{slice?.primary?.title}</RevealTitle>
              </h1>
            )}

            {isFilled.richText(slice?.primary?.subtitle) && (
              <div className={sharedStyles.copy}>
                <RichText field={slice?.primary?.subtitle} />
              </div>
            )}
          </div>

          {(slice?.primary?.show_filters && (allGroupedEvents.length > 0)) && (
            <ScheduleFilters
              groupedEvents={allGroupedEvents}
              onFilterChange={handleFilterChange}
            />
          )}

          {(slice?.primary?.show_link_to_event_schedule) && (pageData?.type === 'event') && (pageData?.uid) && (
            <MoreLink link={`/${Settings.eventsPrefix}/${pageData?.uid}/schedule`} text={"See full schedule"} slice={slice} />
          )}

          {isFilled.link(slice?.primary?.full_schedule_link) && (
            <MoreLink link={slice?.primary?.full_schedule_link} slice={slice} />
          )}
        </div>

        {/* schedule */}
        {filteredEvents.length > 0 ? (
          <div className={clsx(styles.panels)}>
            {filteredEvents.map((dateGroup, dateIndex) => {

              // Now iterate over the label groups within the date
              return dateGroup.labelGroups.map((labelGroup, labelIndex) => {
                const panelKey = getPanelKey(dateIndex, labelIndex);
                const isOpen = openIndexes.includes(panelKey);

                return (
                  <div
                    className={clsx(styles.panel, { [styles.opened]: isOpen })}
                    key={`label-group-${panelKey}`}
                  >
                    {/* The header uses the label or the date */}
                    <div
                      className={clsx(styles.header)}
                      onClick={() => togglePanel(panelKey)}
                      style={{ backgroundColor: slice?.primary?.collapsible_group_background_color }}
                    >
                      <h2>{labelGroup.panelTitle}</h2>
                      <Chevron className={clsx(styles.chevron, { [styles.rotated]: isOpen })} />
                    </div>

                    <div className={clsx(styles.body)}>
                      <div className={clsx(styles.table)}>
                        {/* Iterate over events within the label group */}
                        {labelGroup.events.map((event, eventIndex) => {

                          // Determine the URL/ID for the "Tickets" link
                          const ticketsLink = event?.linked_session?.data?.tickets_link || event?.buy_tickets_link;
                          const ticketsSquadUpId = event?.linked_session?.data?.squadup_event_id || event?.squadup_event_id;

                          const hasMoreInfoLink = isFilled.contentRelationship(event?.linked_session) || isFilled.link(event?.more_info_link);
                          const hasTicketsAction = isFilled.contentRelationship(event?.linked_session) || isFilled.link(event?.buy_tickets_link) || isFilled.keyText(ticketsSquadUpId);

                          // Get the best available start date/time
                          const startDateTime = getStartDateTime(event);
                          const hasStartTime = hasTimeComponent(event, 'start_date_and_time');

                          // Format time - only show if we have a time component
                          let timeDisplay = 'N/A';
                          if (hasStartTime) {
                            const linkedSession = event?.linked_session?.data;
                            const startForTime = linkedSession?.start_date_and_time || event?.start_date_and_time;
                            if (startForTime) {
                              const EST_TIMEZONE = 'America/New_York';
                              timeDisplay = formatInTimeZone(parseISO(startForTime), EST_TIMEZONE, 'h:mma');
                            }
                          } else if (startDateTime) {
                            // If we only have a date (no time), show nothing or 'N/A'
                            timeDisplay = 'N/A';
                          }

                          return (
                            <div className={clsx(styles.row)} key={`event-${panelKey}-${eventIndex}`}>
                              <div className={clsx(styles.col)}>
                                <span className={styles.time}>
                                  {timeDisplay}
                                </span>
                                {event?.duration && (
                                  <span className={styles.duration}>
                                    {event.duration}
                                  </span>
                                )}
                              </div>
                              <div className={clsx(styles.col, styles.title)}>
                                <span>{event?.title}</span>
                              </div>
                              <div className={clsx(styles.col, styles.description)}>
                                {isFilled.richText(event?.description) && (
                                  <RichText field={event.description} />
                                )}

                                {(hasMoreInfoLink || hasTicketsAction) && (
                                  <div className={clsx(styles.footer)}>

                                    {/* More Info Link (uses regular Link/PrismicNextLink logic, NOT ConditionalLink) */}
                                    {isFilled.contentRelationship(event?.linked_session) ? (
                                      <Link href={formatLink(event?.linked_session)} className={clsx(styles.link)}>
                                        {event?.more_info?.text || "More info"}
                                      </Link>
                                    ) : isFilled.link(event?.more_info_link) ? (
                                      <Link href={formatLink(event?.more_info_link)} className={clsx(styles.link)}>
                                        {event?.more_info?.text || "More info"}
                                      </Link>
                                    ) : null}

                                    {/* Buy Tickets Link (Uses ConditionalLink to handle squadup_event_id) */}
                                    {hasTicketsAction && (
                                      <ConditionalLink
                                        url={ticketsLink}
                                        squadupeventid={ticketsSquadUpId}
                                        target={formatTarget(ticketsLink)}
                                        className={clsx(styles.link, styles.tickets)}
                                      >
                                        {event?.linked_session?.data?.tickets_link?.text || event?.buy_tickets_link?.text || "Tickets"}
                                      </ConditionalLink>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              });
            })}
          </div>
        ) : (
          <p className={styles.noEvents}>No events scheduled.</p>
        )}

        {(slice?.primary?.show_link_to_event_schedule) && (pageData?.type === 'event') && (pageData?.uid) && (
          <MoreLink link={`/${Settings.eventsPrefix}/${pageData?.uid}/schedule`} text={"See full schedule"} slice={slice} mobile={true} />
        )}

        {isFilled.link(slice?.primary?.full_schedule_link) && (
          <MoreLink link={slice?.primary?.full_schedule_link} slice={slice} mobile={true} />
        )}

      </div>
    </BackgroundImage>
  );
}

export default Schedule;